import {Typography, makeStyles} from '@material-ui/core';
import {getPlanConfigurationRequestPayload} from 'Quoting/api/apiRequestHelpers';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {SelectionItem, SelectionList, SelectionListProps} from 'Quoting/components/shared/SelectionList';
import {retrievePlanDataToAdd, updateSelectedPlan} from 'Quoting/ducks/createQuote';
import {PlanWithCoverages, RetreivedPlan} from 'Quoting/models/DTOs/QuoteForm';
import {Product} from 'Quoting/models/Quoting';
import {safeRenderValue} from 'Quoting/utils/utilities';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import React, {useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';

const usePlanSelectionStyles = makeStyles(() => ({
  planLabel: {
    display: 'flex',
    flexDirection: 'column',
    '& .lbl-main': {
      color: '#0033A0',
      fontSize: '1.125rem',
      lineHeight: '1.35rem',
      fontWeight: 600,
    },
    '& .lbl-secondary': {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: '1.05rem',
      textAlign: 'left',
      color: '#333333',
    },
  },

  planSelectionTitle: {
    color: '#2f3847',
    fontSize: '1.125rem',
    lineHeight: '1.35rem',
    fontWeight: 600,
  },
  helperText: {
    color: '#f44336',
    paddingTop: '0.5rem',
  },
}));

const PlanLabel = ({main, secondary, helperText}: {main: string; secondary?: string; helperText?: string}) => {
  const classes = usePlanSelectionStyles();
  return (
    <div className={classes.planLabel}>
      <Typography variant="body1" className="lbl-main">
        {main}
      </Typography>
      {secondary && (
        <React.Fragment>
          <Typography variant="body2" className="lbl-secondary">
            Plan Description: {secondary}
          </Typography>
          {helperText && (
            <Typography component="span" className={classes.helperText}>
              {helperText}
            </Typography>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

const PlanSelectionTitle = ({selectedProduct}: {selectedProduct?: Product}) => {
  const classes = usePlanSelectionStyles();
  return (
    <span className={classes.planSelectionTitle}>
      PLAN SELECTION: {safeRenderValue(`${selectedProduct?.name} (${selectedProduct?.code})`)}
    </span>
  );
};

type PlanSelectionProps = Pick<SelectionListProps<PlanWithCoverages>, 'onCancel'>;

const fetchPlanError = (err: string) =>
  err.includes('must be between') ? `Minimum of ${err.match(/\d+/)[0]} eligible lives required.` : err;

export const PlanSelection = React.memo((props: PlanSelectionProps) => {
  const {selectedProduct, plansWithCoverages} = useSelector((state: ReducerState) => state.quoteForm);

  const {quote} = useQuoteField();
  const dispatch = useDispatch();

  const items = useMemo(
    () =>
      plansWithCoverages.key === 'Loaded'
        ? plansWithCoverages?.Data?.map<RetreivedPlan & SelectionItem>(plan => ({
            ...plan,
            disabled: plan.isDisabled,
            label: (
              <PlanLabel
                main={plan.name}
                secondary={plan.description}
                helperText={
                  plan.isDisabled
                    ? fetchPlanError(plan.error)
                    : !plan.isDisabled && !plan.propGenSupported
                    ? 'A proposal cannot be generated for this product in this state.'
                    : ''
                }
              />
            ),
          }))
        : [],
    [plansWithCoverages.key, quote]
  );

  return renderAsyncView(plansWithCoverages, plansWithCoveragesData => (
    <SelectionList
      items={items}
      identifier="key"
      onCancel={props.onCancel}
      onContinue={(key: string) => {
        const selectedPlan = plansWithCoveragesData.find(plan => plan.key === key);
        dispatch(updateSelectedPlan({planIndex: 0, planKey: selectedPlan.key}));
        const selectedPlanCd = selectedPlan?.planCode;
        const requestPayload = getPlanConfigurationRequestPayload(
          quote,
          selectedPlanCd,
          selectedPlan?.coverages
            ?.filter(c => c.applicability === 'Mandatory' || c.applicability === 'Optional')
            .map(c => c.code)
        );
        dispatch(retrievePlanDataToAdd(requestPayload));
      }}
      header={{
        secondaryTitle: <PlanSelectionTitle selectedProduct={selectedProduct} />,
      }}
    />
  ));
});
